<template>
  <v-card outlined elevation="0">
    <v-card-text>
      <v-form ref="form">
        <v-row>
          <!-- Seção de Imagem de Capa -->
          <v-col cols="12">
            <v-card class="mb-4" outlined>
              <v-img
                v-if="organization.cover || previewCover"
                :src="previewCover || organization.cover"
                :aspect-ratio="21 / 9"
                class="grey lighten-2"
                max-height="260px"
              >
                <template v-slot:placeholder>
                  <v-row
                    class="fill-height ma-0"
                    align="center"
                    justify="center"
                  >
                    <v-progress-circular
                      indeterminate
                      color="primary"
                    ></v-progress-circular>
                  </v-row>
                </template>
              </v-img>
              <v-card-actions class="py-4">
                <v-file-input
                  v-model="coverFile"
                  accept="image/*"
                  label="Selecionar nova imagem de capa"
                  prepend-icon="mdi-camera"
                  :hide-details="true"
                  outlined
                  dense
                  @change="handleCoverPreview"
                ></v-file-input>
              </v-card-actions>
            </v-card>
          </v-col>

          <!-- Campos de Informação -->
          <v-col cols="12" md="6">
            <v-text-field
              v-model="formData.name"
              label="Nome da Organização"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field v-model="formData.slug" label="Slug" outlined dense />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="formData.email"
              label="Email"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="6">
            <phone-input
              :phone="formData.phone.number"
              label="Telefone"
              @update:phone="(e) => (formData.phone.number = e)"
              :ddi="formData.phone.ddi"
              @update:ddi="(e) => (formData.phone.ddi = e)"
              dense
              outlined
              :optional="true"
            />
          </v-col>
          <v-col
            v-if="organization.commercial_representative.name"
            cols="12"
            md="12"
            class="pt-0"
          >
            <v-card outlined elevation="0" class="pa-3">
              <div class="d-flex align-center">
                <base-avatar
                  v-if="organization.commercial_representative.photo"
                  :src="organization.commercial_representative.photo"
                  size="35"
                />
                <base-avatar
                  v-else
                  :seed="organization.commercial_representative.id"
                  size="45"
                />
                <div class="ml-2 d-flex flex-column">
                  <span class="text-h7">{{
                    organization.commercial_representative.name
                  }}</span>
                  <span class="text--disabled caption">{{
                    organization.commercial_representative.email
                  }}</span>
                </div>
              </div>
            </v-card>
          </v-col>

          <!-- Representante Comercial -->
          <v-col cols="12" md="12">
            <phone-input
              label="Telefone do Representante Comercial"
              :phone="formData.commercial_representative.phone"
              @update:phone="
                (e) => (formData.commercial_representative.phone = e)
              "
              :ddi="formData.commercial_representative.ddi"
              @update:ddi="(e) => (formData.commercial_representative.ddi = e)"
              dense
              outlined
              :optional="true"
            />
          </v-col>

         
        </v-row>

        <!-- Botões de Ação -->
        <v-row class="mt-4">
          <v-col cols="12" class="d-flex justify-end">
            <v-btn
              color="primary"
              @click="handleUpdateOrganization"
              :loading="loading"
            >
              Atualizar Organização
            </v-btn>
          </v-col>
        </v-row>

        <!-- Novo Alerta de Erro -->
        <v-alert
          v-if="error"
          type="error"
          class="mt-4"
          dismissible
          @input="error = null"
        >
          {{ error }}
        </v-alert>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import organizationsService from "@/services/admin/organizations";
import PhoneInput from "@/components/global/PhoneInput.vue";

export default {
  name: "OrganizationDetails",

  components: {
    PhoneInput,
  },

  props: {
    organization: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      coverFile: null,
      previewCover: null,
      error: null,
      formData: {
        name: "",
        slug: "",
        email: "",
        phone: {
          number: "",
          ddi: 55,
        },
        commercial_representative: {
          phone: "",
          ddi: 55,
        },
      },
    };
  },

  watch: {
    organization: {
      handler(newVal) {
        if (newVal) {
          this.formData = { ...newVal };
          
          if (newVal.phone) {
            this.formData.phone = {
              number: newVal.phone.replace(/\D/g, ''),
              ddi: newVal.ddi || 55
            };
          }

          if (newVal.CommercialResponsible) {
            this.formData.commercial_representative = {
              phone: newVal.CommercialResponsible.phone.replace(/\D/g, ''),
              ddi: newVal.CommercialResponsible.ddi || 55
            };
          }
        }
      },
      immediate: true,
    },
  },

  methods: {
    handleCoverPreview(file) {
      if (!file) {
        this.previewCover = null;
        return;
      }
      this.previewCover = URL.createObjectURL(file);
    },

    async handleUpdateOrganization() {
      try {
        this.error = null;
        this.loading = true;
        const organizationId = this.$route.params.id;

        const dataToSend = {
          ...this.formData,
          phone: this.formData.phone.number,
          ddi: this.formData.phone.ddi,
          commercial_representative: {
            ...this.formData.commercial_representative,
            phone: this.formData.commercial_representative.phone, 
            ddi: this.formData.commercial_representative.ddi
          }
        };

        if (this.coverFile) {
          const formData = new FormData();
          formData.append("file", this.coverFile);

          const response = await organizationsService.uploadOrganizationCoverPhoto(
            organizationId,
            formData
          );

          if (!response.success) {
            throw new Error(response.message || "Erro ao fazer upload da imagem");
          }

          dataToSend.cover = response.coverUrl;
        }

        await organizationsService.updateOrganization(organizationId, dataToSend);

        this.coverFile = null;
        this.previewCover = null;
        this.$emit("refresh");
        this.$toast.success("Organização atualizada com sucesso!");
      } catch (error) {
        console.error("Erro ao atualizar organização:", error);
        this.error = error.response?.data?.error?.message || error.message || "Erro ao atualizar organização";
        this.$toast.error(this.error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
